<script>
import VuetifyColourClass from '@/constants/core/VuetifyColourClass'
import ButtonConstants from '@/constants/components/BaseButton'
import extractKeys from '@/utils/extract-keys'

const {
  dimensionsMap,
  colorMap,
  hoverColorMap,
  iconSizeMap,
  textColorMap,
  textSizeClassMap,
  iconColorMap,
  iconHoverColorMap,
} = ButtonConstants

export default {
  inheritAttrs: false,
  props: {
    elevation: {
      type: [String, Number],
      default: 0,
    },
    circle: Boolean,
    borderRadius: {
      type: String,
      default: '',
    },

    // BEHAVIOUR PROPS
    disabled: Boolean,
    active: Boolean,

    // SIZE PROPS
    size: {
      type: String,
      default: 'md',
      validator: (val) =>
        Object.keys(dimensionsMap.default).includes(val) || val === '',
    },
    height: {
      type: [String, Number],
      default: '',
    },
    width: {
      type: [String, Number],
      default: '',
    },
    iconSize: {
      type: [String, Number],
      default: null,
    },
    sm: Boolean,
    md: Boolean,
    lg: Boolean,
    xl: Boolean,
    xxl: Boolean,

    // COLOR PROPS
    color: {
      type: String,
      default: '',
    },
    hoverColor: {
      type: String,
      default: '',
    },
    textColor: {
      type: String,
      default: '',
    },
    textHoverColor: {
      type: String,
      default: '',
    },
    iconColor: { type: String, default: '' },
    iconHoverColor: { type: String, default: '' },

    // VARIANT PROPS
    secondary: Boolean,
    tertiary: Boolean,
    tertiaryBlue: Boolean,
    text: Boolean,
    textBlue: Boolean,
    dot: Boolean,
    fab: Boolean,

    // ICON PROPS
    prependIcon: { type: String, default: '' },
    appendIcon: { type: String, default: '' },
    icon: { type: String, default: '' },
  },
  data: () => ({
    isActive: false,
  }),

  computed: {
    btnListeners() {
      return { ...this.$listeners, click: this.handleClick }
    },

    hideRipple() {
      return this.text || this.textBlue || this.fab
    },

    isTextBtn() {
      return this.text || this.textBlue
    },

    configKeys() {
      const allKeys = Object.values(ButtonConstants).flatMap(extractKeys)
      return [...new Set(allKeys)].filter((key) => key !== 'default')
    },

    activeProps() {
      const cleanedProps = Object.keys(this.$props).filter((key) =>
        this.configKeys.includes(key)
      )

      return cleanedProps.filter((key) => this[key])
    },

    buttonClasses() {
      return [
        this.textSizeClass,
        this.textColorClass,
        this.fab ? 'pa-0' : 'pa-2',
        {
          'secondary-button': this.secondary,
          'disable-button': this.disabled,
          'text-button': this.text || this.textBlue,
          circle: this.circle,
          'btn-border-radius': this.borderRadius?.length,
        },
      ]
    },

    setDynamicVars() {
      return {
        '--before-color':
          this.text || this.textBlue ? 'transparent' : this.btnHoverColor,
        '--border-radius': this.borderRadius,
      }
    },

    dimensions() {
      const activeSizeMap = this.getMappedValue(dimensionsMap)

      return this.getMappedValue(
        { ...activeSizeMap, default: activeSizeMap.md },
        this.size?.length,
        activeSizeMap[this.size]
      )
    },

    btnColor() {
      return this.getMappedValue(
        colorMap,
        this.color?.length,
        VuetifyColourClass[this.color] || this.color
      )
    },

    btnHoverColor() {
      return this.getMappedValue(
        hoverColorMap,
        this.hoverColor?.length,
        this.hoverColor
      )
    },

    textColorClass() {
      const { text } = VuetifyColourClass

      // text variant color controlled by color/hoverColor props
      if (this.text || this.textBlue) return ''

      return this.getMappedValue(
        textColorMap,
        this.textColor?.length,
        text[this.textColor]
      )
    },
    textSizeClass() {
      return this.getMappedValue(
        textSizeClassMap,
        this.size?.length,
        textSizeClassMap[this.size]
      )
    },
    calcIconSize() {
      return (
        this.iconSize ||
        this.getMappedValue(iconSizeMap, this.size === 'xxl', iconSizeMap.xxl)
      )
    },
    btnIconHoverColor() {
      return this.getMappedValue(
        iconHoverColorMap,
        this.iconHoverColor?.length,
        this.iconHoverColor
      )
    },
    btnIconColor() {
      return this.getMappedValue(
        iconColorMap,
        this.iconColor?.length,
        this.iconColor
      )
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick) // Listen for outside clicks
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick) // Clean up listener
  },

  methods: {
    getMappedValue(map, overideCondition = false, overideValue = '') {
      if (overideCondition) return overideValue

      const matchingKey = this.activeProps.find((key) => map[key])

      return map[matchingKey] ?? map.default
    },
    handleClick(event) {
      if (this.active) this.isActive = !this.isActive

      this.$emit('click', event)
    },
    deactivate() {
      if (this.isActive) this.isActive = false
    },
    handleOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.deactivate()
      }
    },
  },
}
</script>

<template>
  <v-hover v-slot="{ hover }">
    <v-btn
      v-bind="{ ...$attrs }"
      :class="buttonClasses"
      :style="setDynamicVars"
      :min-height="height || dimensions.height"
      :min-width="width || dimensions.width"
      :elevation="elevation"
      :ripple="!hideRipple"
      :text="isTextBtn"
      :color="hover || isActive ? btnHoverColor : btnColor"
      v-on="btnListeners"
    >
      <v-icon
        v-if="prependIcon.length || dot"
        :size="calcIconSize"
        :color="hover || isActive ? btnIconHoverColor : btnIconColor"
        class="mr-2"
        >{{ dot ? 'mdi-circle' : prependIcon }}</v-icon
      >

      <!-- Ignore text if fab -->
      <slot v-if="!fab" />
      <v-icon
        v-else
        :size="calcIconSize"
        :color="hover || isActive ? btnIconHoverColor : btnIconColor"
        >{{ icon }}</v-icon
      >

      <v-icon
        v-if="appendIcon.length"
        :size="calcIconSize"
        :color="hover || isActive ? btnIconHoverColor : btnIconColor"
        class="ml-2"
        >{{ appendIcon }}</v-icon
      >
    </v-btn>
  </v-hover>
</template>

<style scoped lang="scss">
@import '@/scss/_colors.scss';
.secondary-button {
  border: 1px solid $color-grey-400 !important;
}

.disable-button {
  pointer-events: none;
  cursor: pointer;
  opacity: 0.38;
}

.text-button::before {
  background-color: transparent !important;
}

.v-btn::before {
  background-color: var(--before-color, transparent) !important;
}

.btn-border-radius {
  border-radius: var(--border-radius, 4px) !important;
}

.circle {
  border-radius: 50%;
}
</style>
